import React, { useCallback, useEffect, useRef, useState } from "react";
import { ChevronLeftIcon, ChevronRightIcon } from "lucide-react";
import type { Aspect, Film } from "../types/film";
import { FadeUp } from "./FadeUp";
import { LandscapeCard } from "./cards/LandscapeCard";
import { ReelCard } from "./cards/ReelCard";
import { PosterCard } from "./cards/PosterCard";
import { TrailerCard } from "./cards/TrailerCard";
interface FilmRowProps {
  title: string;
  subtitle?: string;
  aspect: Aspect;
  films: Film[];
  id?: string;
  hoverExpand?: boolean;
}
const widths: Record<Aspect, string> = {
  // Phones/tablets: swipeable with peeking cards. Desktop: cards share the full content width.
  "16:9": "w-[82vw] sm:w-[46vw] lg:w-auto lg:min-w-0 lg:flex-1",
  "9:16": "w-[56vw] sm:w-[32vw] lg:w-auto lg:min-w-0 lg:flex-1",
  "2:3": "w-[64vw] sm:w-[38vw] lg:w-auto lg:min-w-0 lg:flex-1",
  "2.39:1": "w-[86vw] sm:w-[60vw] lg:w-auto lg:min-w-0 lg:flex-1"
};
export function FilmRow({
  title,
  subtitle,
  aspect,
  films,
  id,
  hoverExpand = true
}: FilmRowProps) {
  const scroller = useRef<HTMLUListElement>(null);
  const [canPrev, setCanPrev] = useState(false);
  const [canNext, setCanNext] = useState(true);
  const expands = aspect === "16:9" && hoverExpand;
  const update = useCallback(() => {
    const el = scroller.current;
    if (!el) return;
    setCanPrev(el.scrollLeft > 4);
    setCanNext(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
  }, []);
  useEffect(() => {
    update();
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, [update]);
  const scrollBy = (dir: 1 | -1) => {
    const el = scroller.current;
    if (!el) return;
    el.scrollBy({
      left: dir * el.clientWidth * 0.8,
      behavior: "smooth"
    });
  };
  const renderCard = (film: Film, i: number) => {
    if (aspect === "16:9") return <LandscapeCard film={film} first={i === 0} last={i === films.length - 1} expand={hoverExpand} data-id="element-82" data-mp="3e7xturrbxmpv" />;
    if (aspect === "9:16") return <ReelCard film={film} data-id="element-83" data-mp="2oizgxjw4gt6m" />;
    if (aspect === "2:3") return <PosterCard film={film} data-id="element-84" data-mp="2ohfzhpyqcc39" />;
    return <TrailerCard film={film} data-id="element-85" data-mp="2jlh4hjiwbvtv" />;
  };
  return <section id={id} className="group/row relative hover:z-20 focus-within:z-20" aria-label={title} data-id="element-86" data-mp="2jil65u34z54w">
      <FadeUp data-id="element-87" data-mp="2ybooi3ojxmwo">
        <div className="container-x mb-4 flex items-baseline gap-4" data-id="element-88" data-mp="2tivkxj4tkkm6">
          <h2 className="font-serif text-[20px] leading-tight tracking-[-0.01em] text-white md:text-[24px]" data-id="element-89" data-mp="2thkh5jbdjcv2">{title}</h2>
          {subtitle && <span className="hidden font-mono text-[12px] tracking-[0.1em] text-foot sm:inline" data-id="element-90" data-mp="32b790ycgnvyz">{subtitle}</span>}
          <a href="#book" className="font-mono text-[12px] tracking-[0.1em] text-signal opacity-0 transition-opacity duration-150 focus:opacity-100 group-hover/row:opacity-100" data-id="element-91" data-mp="3h86gmm7bbotg">
            START YOUR FILM ›
          </a>
        </div>
        {subtitle && <p className="container-x -mt-2 mb-4 font-mono text-[11px] tracking-[0.1em] text-foot sm:hidden" data-id="element-92" data-mp="1hvc1k153cwpi">{subtitle}</p>}

        <div className="relative" data-id="element-93" data-mp="3ti5tfn4opgak">
          <ul ref={scroller} onScroll={update} tabIndex={0} aria-label={`${title} — scroll horizontally`} className={`row-pad no-scrollbar pointer-events-none flex snap-x snap-proximity gap-3 overflow-x-auto overflow-y-hidden overscroll-x-contain md:gap-4 ${expands ? "-my-6 py-6 md:-mb-32 md:pb-32" : "-my-3 py-3"}`} data-id="element-94" data-mp="3tj7gkpyz5t8n">
            {films.map((film, i) => <li key={film.id} className={`pointer-events-auto shrink-0 snap-start ${widths[aspect]}`} data-id="element-95" data-mp="3euaxzyzakxts" data-mpi={i}>
                {renderCard(film, i)}
              </li>)}
          </ul>

          <button type="button" onClick={() => scrollBy(-1)} aria-label={`Scroll ${title} left`} disabled={!canPrev} className="absolute inset-y-0 left-0 z-30 hidden w-12 items-center justify-center bg-ink/70 text-white opacity-0 transition-opacity duration-150 focus:opacity-100 disabled:pointer-events-none disabled:!opacity-0 group-hover/row:opacity-100 md:flex" data-id="element-96" data-mp="35suywndxgi4v">
            <ChevronLeftIcon className="h-8 w-8" data-id="element-97" data-mp="2v739ikpugpu2" />
          </button>
          <button type="button" onClick={() => scrollBy(1)} aria-label={`Scroll ${title} right`} disabled={!canNext} className="absolute inset-y-0 right-0 z-30 hidden w-12 items-center justify-center bg-ink/70 text-white opacity-0 transition-opacity duration-150 focus:opacity-100 disabled:pointer-events-none disabled:!opacity-0 group-hover/row:opacity-100 md:flex" data-id="element-98" data-mp="2r3or5nefnxdg">
            <ChevronRightIcon className="h-8 w-8" data-id="element-99" data-mp="27gqqoet298op" />
          </button>
        </div>
      </FadeUp>
    </section>;
}